<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-end align-center">
      <v-container class="onest-font">
        <a
          @click.prevent="goBack"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold mt-2 text-5xl">Roteiros</h1>
        <div class="text-lg mt-2 flex flex-col">
          <span v-if="projeto" class="font-bold">
            Projeto: <span class="font-medium">{{ projeto.nome }}</span>
          </span>
          <span v-if="projeto" class="font-bold">
            Validação:
            <span class="font-medium">{{ projeto.validacao }}</span>
          </span>
        </div>
      </v-container>
      <v-container class="onest-font d-flex justify-end gap-2">
        <v-btn
          v-if="isDeveloper"
          variant="flat"
          prepend-icon="mdi-plus"
          color="primary"
          @click="openCreateRoteiro"
        >
          Criar Roteiro
        </v-btn>
      </v-container>
    </div>
    <v-container>
      <div class="search-bar w-full pb-4">
        <v-text-field
          v-model="searchQuery"
          variant="solo"
          flat
          hide-details
          class="search-input"
          placeholder="Buscar roteiro por ator, objetivo ou situação"
          aria-label="Buscar roteiro"
          append-inner-icon="mdi-magnify"
        ></v-text-field>
      </div>

      <!-- Menu de Filtros por Status -->
      <div class="status-filters w-full pb-4 onest-font">
        <v-chip-group
          v-model="selectedStatus"
          column
          mandatory
          selected-class="bg-primary text-white"
        >
          <v-chip value="TODOS" variant="outlined" class="font-medium">
            Todos
          </v-chip>
          <v-chip
            v-for="(label, value) in statusOptions"
            :key="value"
            :value="value"
            variant="outlined"
            class="font-medium"
          >
            {{ label }}
          </v-chip>
        </v-chip-group>
      </div>

      <div v-if="loading" class="py-12 text-center text-gray-500 font-medium">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-4"
        ></v-progress-circular>
        <p>Carregando roteiros...</p>
      </div>

      <div v-else>
        <v-row v-if="filteredRoteiros.length > 0">
          <v-col>
            <div class="my-4">
              <table class="w-full border-separate border-spacing-y-2">
                <thead class="p-2">
                  <tr class="">
                    <th class="p-4 font-medium text-left">Ator</th>
                    <th class="p-4 font-medium text-left">Situação</th>
                    <th class="p-4 font-medium text-left">Contas de Teste</th>
                    <th class="p-4 font-medium text-left">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="roteiro in filteredRoteiros"
                    :key="roteiro.id_roteiro"
                    class="hover:bg-blue-50/50 transition-colors bg-white shadow-sm"
                  >
                    <td class="py-4 px-6 text-gray-600 rounded-l-xl">
                      {{
                        roteiro.tb_ator
                          ? roteiro.tb_ator.nome_ator
                          : 'Não definido'
                      }}
                    </td>
                    <td class="py-4 px-6 text-left">
                      <v-chip
                        :color="getStatusColor(roteiro.situacao)"
                        class="font-medium"
                        size="small"
                        variant="tonal"
                      >
                        {{ formatarStatus(roteiro.situacao) }}
                      </v-chip>
                    </td>
                    <td class="py-4 px-6 text-gray-600">
                      <v-chip>
                        {{
                          roteiro.tb_conta_teste?.filter(
                            (c) => c.id_usuario_externo,
                          ).length
                        }}/{{ roteiro.tb_conta_teste?.length || 0 }}
                        contas associadas
                      </v-chip>
                    </td>
                    <td class="py-4 px-6 text-left rounded-r-xl">
                      <v-tooltip location="bottom" text="Visualizar">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            color="grey-darken-1"
                            variant="text"
                            size="small"
                            class="text-center font-normal rounded-full mr-2"
                            @click="visualizarRoteiro(roteiro)"
                          >
                            <v-icon>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-tooltip
                        v-if="isDeveloper"
                        location="bottom"
                        text="Editar"
                      >
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            color="primary"
                            variant="text"
                            size="small"
                            class="text-center font-normal rounded-full"
                            @click="editarRoteiro(roteiro)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col
            class="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm mt-4"
          >
            <div class="text-6xl mb-4">📝</div>
            <p class="text-lg">
              Nenhum roteiro encontrado para o filtro selecionado.
            </p>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';

import store from '@/store/store';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  data() {
    return {
      searchQuery: '',
      projeto: { nome: 'Carregando...', validacao: 'Carregando...' },
      idProjeto: '',
      roteiros: [],
      selectedStatus: 'TODOS',
      statusOptions: {
        EM_EDICAO: 'Em Edição',
        EM_VALIDACAO: 'Em Validação',
        VALIDO: 'Válido',
        PRECISA_CORRECAO: 'Precisa Correção',
        CORRIGIDO: 'Corrigido',
        ATIVO: 'Ativo',
        INATIVO: 'Inativo',
      },
      loading: true,
    };
  },
  computed: {
    isDeveloper() {
      return localStorage.getItem('funcao') === 'Desenvolvedor';
    },
    filteredRoteiros() {
      let result = this.roteiros;

      // Filtro por Status
      if (this.selectedStatus !== 'TODOS') {
        result = result.filter(
          (roteiro) => roteiro.situacao === this.selectedStatus,
        );
      }

      if (!this.searchQuery) {
        return result;
      }
      const query = this.searchQuery.toLowerCase();

      return result.filter((roteiro) => {
        const status = this.formatarStatus(roteiro.situacao).toLowerCase();
        const ator = roteiro.tb_ator
          ? roteiro.tb_ator.nome_ator.toLowerCase()
          : '';
        const objetivo = roteiro.objetivo_do_roteiro
          ? roteiro.objetivo_do_roteiro.toLowerCase()
          : '';

        return (
          status.includes(query) ||
          ator.includes(query) ||
          objetivo.includes(query)
        );
      });
    },
  },
  async mounted() {
    this.idProjeto = this.$route.params.idProjeto;
    this.loading = true;
    try {
      await this.fetchProjetoInfo();
    } catch (error) {
      console.error('Erro ao buscar dados da página:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    /**
     * @method Obtém informações sobre o projeto e validação recente
     */
    fetchProjetoInfo() {
      return axios
        .get(`${apiUrl}/projetos/listaprojeto/${this.idProjeto}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        })
        .then((res) => {
          if (res.data) {
            this.projeto.nome = res.data.nome || 'Não foi possível carregar';

            // Pega a validação mais recente através da tabela associativa
            const associacao = res.data.tb_projetos_envolvidos?.[0];
            const validacao = associacao?.tb_validacao;

            this.projeto.validacao =
              validacao?.nome_validacao || 'Não foi possível carregar';

            // Salva o id_validacao para consultar os roteiros específicos
            if (validacao) {
              this.idValidacao = validacao.id_validacao;
            }

            // Depois de saber o projeto (e a validação), busca os roteiros
            return this.fetchRoteiros();
          }
        })
        .catch((error) => {
          console.error('Erro ao buscar dados do projeto:', error);
          if (error.response && error.response.data) {
            console.error('Detalhes do erro (API):', error.response.data);
            console.error('MENSAGEM DE ERRRO:', error.response.data.error);
            this.$toast.error(
              error.response.data.error ||
                'Erro ao buscar informações do projeto.',
            );
          } else {
            this.$toast.error('Erro ao buscar informações do projeto.');
          }
        });
    },
    /**
     * @method Obtém os roteiros pela API
     */
    fetchRoteiros() {
      // Se tiver idValidacao, podemos mandar via query params (ex: ?id_validacao=123)
      const url = this.idValidacao
        ? `${apiUrl}/api/v1/projetos/${this.idProjeto}/roteiros?id_validacao=${this.idValidacao}`
        : `${apiUrl}/api/v1/projetos/${this.idProjeto}/roteiros`;

      return axios
        .get(url, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        })
        .then((res) => {
          this.roteiros = res.data || [];
        })
        .catch((error) => {
          console.error('Erro ao buscar roteiros:', error);
          if (error.response && error.response.data) {
            console.error('Detalhes do erro (API):', error.response.data);
            console.error(
              'MENSAGEM DE ERRO (Para copiar):',
              error.response.data.error,
            );
            this.$toast.error(
              error.response.data.error ||
                'Erro ao buscar a lista de roteiros.',
            );
          } else {
            this.$toast.error('Erro ao buscar a lista de roteiros.');
          }
        });
    },
    /**
     * Obtém a cor do status
     * @param {string} status - Status do roteiro
     * @returns {string} - Cor do status
     */
    getStatusColor(status) {
      const colors = {
        EM_EDICAO: 'warning',
        EM_VALIDACAO: 'info',
        VALIDO: 'success',
        PRECISA_CORRECAO: 'error',
        CORRIGIDO: 'success',
        ATIVO: 'primary',
        INATIVO: 'grey',
      };

      return colors[status] || 'grey';
    },
    /**
     * Formata o status do roteiro
     * @param {string} status - Status do roteiro
     * @returns {string} - Status formatado
     */
    formatarStatus(status) {
      const labels = {
        EM_EDICAO: 'Em Edição',
        EM_VALIDACAO: 'Em Validação',
        VALIDO: 'Válido',
        PRECISA_CORRECAO: 'Precisa Correção',
        CORRIGIDO: 'Corrigido',
        ATIVO: 'Ativo',
        INATIVO: 'Inativo',
      };

      return labels[status] || 'Desconhecido';
    },
    /**
     * Abre a página de criação de roteiro
     */
    openCreateRoteiro() {
      this.$router.push(`/projects/${this.idProjeto}/roteiros/create`);
    },
    /**
     * Visualiza o roteiro
     * @param {Object} roteiro - Roteiro a ser visualizado
     */
    visualizarRoteiro(roteiro) {
      this.$router.push(
        `/projects/${this.idProjeto}/roteiros/${roteiro.id_roteiro}/revisar`,
      );
    },
    /**
     * Edita o roteiro
     * @param {Object} roteiro - Roteiro a ser editado
     */
    editarRoteiro(roteiro) {
      this.$router.push(
        `/projects/${this.idProjeto}/roteiros/${roteiro.id_roteiro}/edit`,
      );
    },
    /**
     * Volta para a página anterior
     */
    goBack() {
      this.$router.push('/project-validation/' + this.idProjeto);
    },
  },
};
</script>
